import _async_to_generator from "@swc/helpers/src/_async_to_generator.mjs";
import _define_property from "@swc/helpers/src/_define_property.mjs";
import _object_without_properties from "@swc/helpers/src/_object_without_properties.mjs";
import _ts_generator from "@swc/helpers/src/_ts_generator.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cx from "classnames";
import { datadogRum } from "@datadog/browser-rum";
import { Text } from "@cinch-labs/design-system";
import { AuthModal, storePreAuthFavourite } from "@cinch-labs/shared-auth-modal";
import styles from "./vehicle-card.module.css";
import { AdminFeeButton, Card, Price, ProductImage, Subtitle, SpecsList, Title, FavouriteCTA } from "./components";
import { parseVehicleDoorTrim, productDetailUrl, sendFavouritesAnalyticEvent } from "./utils";
import { formatVehicleSpecs, isWindowUserAgentCinchApp } from "../../utils";
import { Actions, Context } from "@cinch-nx/data-search";
import { useFavouritesStore } from "@cinch-nx/data-favourites";
import { useState } from "react";
import dynamic from "next/dynamic";
import { sendPostMessage } from "@cinch-labs/cinchapp-config";
import { Env, readFromEnv } from "@cinch-nx/environments";
import { VehicleImageCarousel } from "./components/vehicle-image-carousel/vehicle-image-carousel";
import { useAdobeTarget } from "@cinch-labs/adobe";
var CompareCTA = dynamic(function() {
    return import("./components/compare-cta/compare-cta");
}, {
    loadableGenerated: {
        webpack: function() {
            return [
                require.resolveWeak("./components/compare-cta/compare-cta")
            ];
        }
    },
    ssr: false
});
var ERROR_MESSAGE = "Image Unavailable";
var penceToPounds = function(n) {
    return "".concat(Math.round(n / 100).toFixed(0));
};
var VehicleCard = function(_param) {
    var id = _param.id, image = _param.image, imageStyles = _param.imageStyles, title = _param.title, subtitle = _param.subtitle, _isPending = _param.isPending, isPending = _isPending === void 0 ? false : _isPending, isUnfavourited = _param.isUnfavourited, unavailable = _param.unavailable, _hasBorder = _param.hasBorder, hasBorder = _hasBorder === void 0 ? true : _hasBorder, _isHoverable = _param.isHoverable, isHoverable = _isHoverable === void 0 ? true : _isHoverable, _isSearchPage = _param.isSearchPage, isSearchPage = _isSearchPage === void 0 ? false : _isSearchPage, _redirectsToFPA = _param.redirectsToFPA, redirectsToFPA = _redirectsToFPA === void 0 ? true : _redirectsToFPA, // customerHasActiveOrder = false,
    onClick = _param.onClick, onAdminFeesClick = _param.onAdminFeesClick, _favourites = _param.favourites, favourites = _favourites === void 0 ? [] : _favourites, userStatus = _param.userStatus, isCinchApp = _param.isCinchApp, vehicleData = _object_without_properties(_param, [
        "id",
        "image",
        "imageStyles",
        "title",
        "subtitle",
        "isPending",
        "isUnfavourited",
        "unavailable",
        "hasBorder",
        "isHoverable",
        "isSearchPage",
        "redirectsToFPA",
        "onClick",
        "onAdminFeesClick",
        "favourites",
        "userStatus",
        "isCinchApp"
    ]);
    var ref, ref1;
    var discountInPence = vehicleData.discountInPence, depositContributionInPence = vehicleData.depositContributionInPence, make = vehicleData.make, model = vehicleData.model, modelYear = vehicleData.modelYear, isAvailable = vehicleData.isAvailable, isReserved = vehicleData.isReserved, fuelType = vehicleData.fuelType, transmissionType = vehicleData.transmissionType, mileage = vehicleData.mileage, vehicleYear = vehicleData.vehicleYear, price = vehicleData.price, priceIncludingAdminFee = vehicleData.priceIncludingAdminFee, quoteRegularPaymentInPence = vehicleData.quoteRegularPaymentInPence, quoteType = vehicleData.quoteType, additionalImages = vehicleData.additionalImages;
    var hasAdminFeeComplianceBeenSet = readFromEnv(Env.AdminFeeCompliance) === "true";
    /**
   * isSaved is hardcoded and passed from above
   * onSave is not used within the parent
   * we have access to favourites at this level
   * we can set is saved here
   * onSave can also be handled at this level
   *
   * */ var ref2 = useState(false), isAuthModalOpen = ref2[0], setIsAuthModalOpen = ref2[1];
    var ref3 = useFavouritesStore(), addFavourite = ref3.addFavourite, removeFavourite = ref3.removeFavourite;
    var isFavourited = favourites.find(function(favourite) {
        return favourite.vehicleId === id;
    });
    var shouldShowCarsOnOffer = Boolean(discountInPence);
    var shouldShowDepositContribution = Boolean(depositContributionInPence);
    var productDetailsLink = productDetailUrl({
        make: make,
        model: model,
        id: id
    });
    var isSold = isAvailable === false;
    var isSearchMultipleImagesFlagEnabled = readFromEnv(Env.SearchMultipleImages) === "true";
    var isVehicleOpenNewTabFlagEnabled = readFromEnv(Env.SearchVehicleOpenNewTab) === "true";
    var isSearchMultipleImagesVariant = ((ref = useAdobeTarget("SEARCH_MULTIPLE_IMAGES_EXPERIMENT_ID")) === null || ref === void 0 ? void 0 : ref.ExperienceId) === "1";
    var isVehicleOpenNewTabVariant = ((ref1 = useAdobeTarget("SEARCH_VEHICLE_OPEN_NEW_TAB_ID")) === null || ref1 === void 0 ? void 0 : ref1.ExperienceId) === "1";
    var shouldShowImageCarousel = isSearchMultipleImagesVariant && isSearchMultipleImagesFlagEnabled && additionalImages && additionalImages.length > 1;
    var shouldOpenInNewTab = isVehicleOpenNewTabFlagEnabled && isVehicleOpenNewTabVariant && !isCinchApp;
    var tabTarget = shouldOpenInNewTab ? "_blank" : "_self";
    var relValue = shouldOpenInNewTab ? "opener" : undefined;
    // const pendingImageDisplay = additionalImages && additionalImages.length > 1
    var vehiclePrice = hasAdminFeeComplianceBeenSet ? priceIncludingAdminFee : price;
    var handleToggleFavourite = function() {
        var _ref = _async_to_generator(function() {
            var messageToSend;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        messageToSend = {
                            make: make,
                            model: model,
                            modelYear: Number(modelYear),
                            vehicleId: id
                        };
                        if (!isFavourited) return [
                            3,
                            2
                        ];
                        return [
                            4,
                            removeFavourite(id)
                        ];
                    case 1:
                        _state.sent();
                        if (!isWindowUserAgentCinchApp) return [
                            2
                        ];
                        sendPostMessage("favouriteVehicleRemoved", messageToSend);
                        datadogRum.addAction(Actions.CINCHAPP_POST_MESSAGE, {
                            message: {
                                type: "favouriteVehicleRemoved",
                                data: messageToSend
                            },
                            service: Context.CINCHAPP,
                            squad: Context.SQUAD
                        });
                        return [
                            2
                        ];
                    case 2:
                        return [
                            4,
                            addFavourite(id)
                        ];
                    case 3:
                        _state.sent();
                        if (!isWindowUserAgentCinchApp) return [
                            2
                        ];
                        sendPostMessage("favouriteVehicleAdded", messageToSend);
                        datadogRum.addAction(Actions.CINCHAPP_POST_MESSAGE, {
                            message: {
                                type: "favouriteVehicleAdded",
                                data: messageToSend
                            },
                            service: Context.CINCHAPP,
                            squad: Context.SQUAD
                        });
                        return [
                            2
                        ];
                }
            });
        });
        return function handleToggleFavourite() {
            return _ref.apply(this, arguments);
        };
    }();
    var handleOnFavouriteClick = function() {
        var _ref = _async_to_generator(function() {
            var pathnameWithSearchParams, ref, vehicleId;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        pathnameWithSearchParams = "".concat(window.location.pathname).concat(window.location.search);
                        vehicleId = (ref = vehicleData === null || vehicleData === void 0 ? void 0 : vehicleData.vehicleId) !== null && ref !== void 0 ? ref : "";
                        storePreAuthFavourite({
                            id: vehicleId,
                            url: pathnameWithSearchParams
                        });
                        sendFavouritesAnalyticEvent(userStatus === "valid", Boolean(isFavourited), {
                            vehicle: vehicleData
                        });
                        if (!(userStatus === "valid")) return [
                            3,
                            2
                        ];
                        return [
                            4,
                            handleToggleFavourite()
                        ];
                    case 1:
                        _state.sent();
                        return [
                            3,
                            3
                        ];
                    case 2:
                        setIsAuthModalOpen(true);
                        _state.label = 3;
                    case 3:
                        return [
                            2
                        ];
                }
            });
        });
        return function handleOnFavouriteClick() {
            return _ref.apply(this, arguments);
        };
    }();
    var handleAuthModalClose = function() {
        setIsAuthModalOpen(false);
    };
    var handleOnClick = function() {
        datadogRum.addAction(Actions.VEHICLE_CARD_CLICKED);
        onClick && onClick();
    };
    var _obj;
    return /*#__PURE__*/ _jsxs(Card, {
        "data-testid": "product-list-card",
        element: "article",
        hasBorder: hasBorder,
        isHoverable: isHoverable,
        isSearchPage: isSearchPage,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles["content"],
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: cx(styles["meta"], styles["hasAdminFees"], (_obj = {}, _define_property(_obj, styles["collapsed"], !hasBorder), _define_property(_obj, styles["pending"], isPending && isSearchPage), _obj)),
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx(Title, {
                                        element: "h3",
                                        className: styles["title"],
                                        fontSizeFixed: "m",
                                        type: "cardTitle",
                                        isPending: isPending,
                                        "aria-describedby": isReserved ? "vehicle-reserved-".concat(id) : undefined,
                                        children: redirectsToFPA ? /*#__PURE__*/ _jsxs("a", {
                                            "data-testid": "product-list-card-link",
                                            href: productDetailsLink,
                                            className: styles["link"],
                                            onClick: handleOnClick,
                                            target: tabTarget,
                                            rel: relValue,
                                            children: [
                                                title,
                                                /*#__PURE__*/ _jsxs("noscript", {
                                                    children: [
                                                        " ",
                                                        subtitle
                                                    ]
                                                })
                                            ]
                                        }) : /*#__PURE__*/ _jsxs(Text, {
                                            "data-testid": "product-list-card-text",
                                            children: [
                                                title,
                                                /*#__PURE__*/ _jsxs("noscript", {
                                                    children: [
                                                        " ",
                                                        subtitle
                                                    ]
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Subtitle, {
                                        isPending: isPending,
                                        fontSize: "s",
                                        children: parseVehicleDoorTrim(subtitle)
                                    }),
                                    /*#__PURE__*/ _jsx(SpecsList, {
                                        isPending: isPending,
                                        listStyleClassName: styles["specsList"],
                                        listItemClassName: styles["specsListItem"],
                                        items: formatVehicleSpecs({
                                            fuelType: fuelType,
                                            transmissionType: transmissionType,
                                            mileage: mileage,
                                            modelYear: Number(vehicleYear || modelYear)
                                        })
                                    })
                                ]
                            }),
                            !!vehiclePrice && /*#__PURE__*/ _jsx(Price, {
                                isPending: isPending,
                                price: vehiclePrice,
                                quoteRegularPaymentInPence: quoteRegularPaymentInPence,
                                quoteType: quoteType,
                                discountInPence: discountInPence,
                                shouldShowCarsOnOfferTag: shouldShowCarsOnOffer,
                                shouldShowDepositContribution: shouldShowDepositContribution,
                                vehicleId: id
                            })
                        ]
                    }),
                    !hasAdminFeeComplianceBeenSet && /*#__PURE__*/ _jsx(AdminFeeButton, {
                        isPending: isPending,
                        onAdminFeeClick: onAdminFeesClick
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles["searchActions"],
                        children: [
                            /*#__PURE__*/ _jsx(CompareCTA, {
                                vehicleId: id,
                                vehicleTitle: title,
                                vehicleImage: image,
                                vehicleMake: make,
                                vehicleModel: model
                            }),
                            /*#__PURE__*/ _jsx(FavouriteCTA, {
                                isPending: isPending,
                                isSaved: favourites.some(function(vehicle) {
                                    return vehicle.vehicleId === id;
                                }),
                                onClick: handleOnFavouriteClick
                            }),
                            /*#__PURE__*/ _jsx(AuthModal, {
                                isOpen: isAuthModalOpen,
                                handleClose: handleAuthModalClose,
                                useBrowserNavigation: true
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: cx(styles["imageContainer"], imageStyles, _define_property({}, styles["imageContainerCollapsed"], !hasBorder)),
                children: [
                    shouldShowImageCarousel ? /*#__PURE__*/ _jsx(VehicleImageCarousel, {
                        images: additionalImages,
                        isPending: isPending,
                        isAvailable: isAvailable,
                        productDetailUrl: productDetailsLink,
                        ctaTitle: title
                    }) : /*#__PURE__*/ _jsx(ProductImage, {
                        alt: "",
                        isAvailable: isAvailable,
                        image: image,
                        unavailableImageMessage: isSold && " " || unavailable || ERROR_MESSAGE,
                        isPending: isPending
                    }),
                    shouldShowCarsOnOffer && !isReserved && !shouldShowDepositContribution && !isSold && /*#__PURE__*/ _jsx(Text, {
                        fontWeight: "semibold",
                        className: cx([
                            styles["badge"],
                            styles["discount"]
                        ]),
                        "data-testid": "vehicle-has-discount",
                        id: "vehicle-discount-".concat(id),
                        children: "\xa3".concat(penceToPounds(discountInPence !== null && discountInPence !== void 0 ? discountInPence : 0), " off")
                    }),
                    isSearchPage && shouldShowDepositContribution && !isReserved && !isSold && /*#__PURE__*/ _jsxs(Text, {
                        fontWeight: "semibold",
                        className: "".concat(styles["badge"], " ").concat(styles["deposit"]),
                        id: "vehicle-deposit-".concat(id),
                        children: [
                            "\xa3".concat(penceToPounds(depositContributionInPence !== null && depositContributionInPence !== void 0 ? depositContributionInPence : 0)),
                            " towards deposit ",
                            /*#__PURE__*/ _jsx("br", {}),
                            "10.9% APR Representative"
                        ]
                    }),
                    isReserved && !isSold && /*#__PURE__*/ _jsx(Text, {
                        fontWeight: "book",
                        className: "".concat(styles["badge"], " ").concat(styles["reserved"]),
                        "data-testid": "vehicle-is-reserved",
                        id: "vehicle-reserved-".concat(id),
                        children: isSearchMultipleImagesFlagEnabled ? "Reserved" : "Currently reserved"
                    }),
                    isSold && /*#__PURE__*/ _jsx(Text, {
                        element: "span",
                        fontSizeFixed: "s",
                        fontWeight: "book",
                        className: "".concat(styles["badge"], " ").concat(styles["sold"]),
                        "data-testid": "vehicle-is-sold",
                        id: "vehicle-sold-".concat(id),
                        children: "Sold"
                    })
                ]
            })
        ]
    });
};
export default VehicleCard;
